import React from 'react';
import styled from 'styled-components';
import { Label, NonCopyableMarkdown, SectionWrapper } from '../common';
import { MultiLineInput } from '../../elements/Input';
import {
  AssessmentTestContext,
  AssessmentTestContextType,
} from '../../../contexts/assessment-test';
import FollowUp from './FollowUp';

export default function TextTemplateSection({
  section,
  stepId,
}: {
  section: Extract<
    AssessmentTestContextType['steps'][number]['sections'][number],
    { type: 'text-template' }
  >;
  stepId: string;
}) {
  const { blanks, answer, content, template } = section;
  const [, dispatch] = React.useContext(AssessmentTestContext);
  const getBlankPlaceholder = React.useCallback(
    (id: string) => {
      return (
        blanks.find((b) => b.id === id)?.content ?? 'Enter your answer here'
      );
    },
    [blanks]
  );
  const getValue = React.useCallback(
    (id: string) => answer.find((a) => a.id === id)?.content || '',
    [answer]
  );

  const setAnswer = React.useCallback(
    (value: Array<{ id: string; content: string }>) => {
      dispatch({
        type: 'CHANGE_ANSWER_TEXT_TEMPLATE',
        payload: { stepId, sectionId: section.id, answer: value },
      });
    },
    [stepId, section.id, dispatch]
  );

  const setValue = React.useCallback(
    (id: string, value: string) => {
      const exists = answer.some((a) => a.id === id);
      if (exists) {
        setAnswer(
          answer.map((a) => (a.id === id ? { ...a, content: value } : a))
        );
      } else {
        setAnswer([...answer, { id, content: value }]);
      }
    },
    [answer, setAnswer]
  );

  // Parse the content to create a structured document with text blocks and gaps
  const documentParts = React.useMemo(() => {
    return parseDocumentContent(template);
  }, [template]);

  return (
    <SectionWrapper>
      {content && <NonCopyableMarkdown md={content} />}
      <TemplateWrapper>
        {documentParts.map((part, idx) =>
          part.type === 'text' ? (
            <NonCopyableMarkdown key={idx} md={part.value} />
          ) : (
            <MultiLineInput
              key={`${part.id}-${idx}`}
              placeholder={getBlankPlaceholder(part.id)}
              value={getValue(part.id)}
              onChange={(e) => setValue(part.id, e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                return false;
              }}
              minRows={1}
              maxRows={15}
            />
          )
        )}
      </TemplateWrapper>
      <Label>Press ↵ for a new line</Label>
      <FollowUp section={section} stepId={stepId} />
    </SectionWrapper>
  );
}

// Parse markdown content and identify gaps by [id] pattern
function parseDocumentContent(
  markdown: string
): Array<{ type: 'text'; value: string } | { type: 'gap'; id: string }> {
  const result: Array<
    { type: 'text'; value: string } | { type: 'gap'; id: string }
  > = [];

  // Split by [id] placeholder pattern
  const regex = /\[\s*([a-zA-Z0-9_-]+)\s*\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(markdown)) !== null) {
    const [full, id] = match;
    const idx = match.index;

    if (idx > lastIndex) {
      const textContent = markdown.slice(lastIndex, idx);
      if (textContent.trim()) {
        result.push({ type: 'text', value: textContent });
      }
    }

    result.push({ type: 'gap', id });
    lastIndex = idx + full.length;
  }

  if (lastIndex < markdown.length) {
    const textContent = markdown.slice(lastIndex);
    if (textContent.trim()) {
      result.push({ type: 'text', value: textContent });
    }
  }

  return result;
}

const TemplateWrapper = styled.div`
  margin-top: 32px;
  border: 2px dashed ${({ theme }) => theme.colors.bgActive};
  padding: 24px;
  border-radius: 16px;
  margin-bottom: 16px;
  background-color: ${({ theme }) => theme.colors.bgPage2};
`;
